import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import {
  paletteForSeed,
  paletteGradients,
  patternForSeed,
  patternOverlays,
  type BookCoverPalette,
  type BookCoverPattern,
} from '@/lib/palette';

/**
 * BookCover — renders either the book's real `book_cover` image or a
 * deterministic generative placeholder keyed off the book's title/id.
 *
 * The placeholder layout mirrors docs/DESIGN.md: gradient background →
 * semi-transparent pattern overlay → dark left spine → title (22% from
 * top) → author (bottom uppercase).
 *
 * When rendering a real image, a subtle skeleton sits behind the `<img>`
 * and cross-fades out once the image has loaded so cover covers never
 * "pop" in — this matches the behaviour of BookCard inside carousels.
 */
export interface BookCoverProps {
  title: string;
  author?: string | null;
  src?: string | null;
  palette?: BookCoverPalette;
  pattern?: BookCoverPattern;
  w?: number;
  h?: number;
  className?: string;
  /** Stable seed used for deterministic palette/pattern selection. */
  seed?: string;
}

export function BookCover({
  title,
  author,
  src,
  palette,
  pattern,
  w = 140,
  h = 200,
  className,
  seed,
}: BookCoverProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset the fade whenever the cover URL changes (e.g. admin swaps out
  // the image on the detail view).
  useEffect(() => {
    setImageFailed(false);
    setImageLoaded(false);
  }, [src]);

  // If the browser already has the image cached the `onLoad` event can
  // fire before React attaches its handler; resync from the DOM on mount.
  const handleImgRef = (el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 0) {
      setImageLoaded(true);
    }
  };

  const resolvedPalette = useMemo(
    () => palette ?? paletteForSeed(seed ?? title),
    [palette, seed, title],
  );
  const resolvedPattern = useMemo(
    () => pattern ?? patternForSeed(seed ?? title),
    [pattern, seed, title],
  );

  if (src && !imageFailed) {
    return (
      <div
        className={cn(
          'relative block overflow-hidden rounded-[10px] bg-or-slate-100 shadow-or-card',
          className,
        )}
        style={{ width: w, height: h }}
      >
        <div
          aria-hidden
          className={cn(
            'absolute inset-0 animate-pulse bg-or-slate-200 transition-opacity duration-500',
            imageLoaded ? 'opacity-0' : 'opacity-100',
          )}
        />
        <img
          ref={handleImgRef}
          src={src}
          alt={title}
          width={w}
          height={h}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
          className={cn(
            'absolute inset-0 block h-full w-full object-cover transition-opacity duration-500',
            imageLoaded ? 'opacity-100' : 'opacity-0',
          )}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[10px] shadow-or-card',
        className,
      )}
      style={{
        width: w,
        height: h,
        backgroundImage: paletteGradients[resolvedPalette],
      }}
      role="img"
      aria-label={author ? `${title} by ${author}` : title}
    >
      {/* Pattern overlay */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ backgroundImage: patternOverlays[resolvedPattern] }}
      />
      {/* Left spine */}
      <div aria-hidden className="absolute top-0 bottom-0 left-0 w-[6px] bg-black/25" />
      {/* Title */}
      <div
        className="relative px-3 font-display text-white"
        style={{
          marginTop: Math.round(h * 0.18),
          fontSize: Math.min(18, Math.max(12, Math.round(w * 0.11))),
          fontWeight: 700,
          lineHeight: 1.12,
          textShadow: '0 1px 2px rgba(0,0,0,0.25)',
        }}
      >
        {title}
      </div>
      {/* Author */}
      {author ? (
        <div
          className="relative mt-auto px-3 pb-3 font-sans uppercase text-white/85"
          style={{
            fontSize: 9,
            letterSpacing: '0.08em',
            fontWeight: 700,
          }}
        >
          {author}
        </div>
      ) : null}
    </div>
  );
}
